import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { useNotifications } from '@/modules/Notifications'
import { getPriceAsync } from '@/modules/PetSitter/viewProfileServiceHelper'

import { DateTime } from 'luxon'

export const PRODUCTS_MAP = {
  petsitter_home_sitting: {
    title: 'nuit',
    titlePlural: 'nuits',
    icon: 'emt-sit-home',
    completeTitle: 'Garde chez le pet sitter',
  },
  customer_home_sitting: {
    title: 'nuit',
    titlePlural: 'nuits',
    icon: 'emt-sit',
    completeTitle: 'Garde chez le propriétaire',
  },
  walk: {
    title: 'promenade',
    titlePlural: 'promenades',
    icon: 'emt-walk',
    completeTitle: 'Promenades',
  },
  visit: {
    title: 'visite',
    titlePlural: 'visites',
    icon: 'emt-visit',
    completeTitle: 'Visites à domicile',
  },
  walk_hour: {
    title: 'promenade',
    titlePlural: 'promenades',
    icon: 'emt-walk',
    completeTitle: 'Promenades',
  },
  visit_hour: {
    title: 'visite',
    titlePlural: 'visites',
    icon: 'emt-visit',
    completeTitle: 'Visites à domicile',
  },
  walk_half_hour: {
    title: 'promenade',
    titlePlural: 'promenades',
    icon: 'emt-walk',
    completeTitle: 'Promenades',
  },
  visit_half_hour: {
    title: 'visite',
    titlePlural: 'visites',
    icon: 'emt-visit',
    completeTitle: 'Visites à domicile',
  },
}

export const getQuantity = (orderDetail) => {
  if (!orderDetail) return 0

  const { productSlug, periods } = orderDetail

  const isHomeSitting = productSlug === 'petsitter_home_sitting' || productSlug === 'customer_home_sitting'

  if (!periods?.length) return 0

  if (isHomeSitting) {
    const firstStart = DateTime.fromISO(periods[0].start).startOf('day')

    const lastEnd = DateTime.fromISO(periods[periods.length - 1].end).startOf('day')

    return Math.max(0, lastEnd.diff(firstStart, 'days').days)
  }

  return periods.length
}

const buildAnimalIds = (animals) => {
  const total = animals?.reduce((acc, a) => acc + a.number, 0) ?? 0

  const safeTotal = Math.max(1, total)

  return Array.from({ length: safeTotal }, (_, i) => i.toString())
}

type UsePetsitterPriceParams = {
  uuid: string
  cartPayload?: any | null
  petsToComplete?: any[]
  animals?: string[]
}

type UsePetsitterPriceReturn = {
  computedCartPayload: any | null
  petsitterPrice: any | undefined
  reduction: number | null | undefined
  quantity: number
  [key: string]: any
}

export const usePetsitterPrice = ({
  uuid,
  cartPayload,
  petsToComplete,
  animals,
}: UsePetsitterPriceParams): UsePetsitterPriceReturn => {
  const [addNotificationError] = useNotifications()

  const computedCartPayload = useMemo(() => {
    if (!cartPayload) return null

    const resolvedAnimals = animals?.length ? animals : buildAnimalIds(petsToComplete)

    return {
      ...cartPayload,
      animals: resolvedAnimals.length > 0 ? resolvedAnimals : ['0'],
      petsitters: [uuid],
    }
  }, [cartPayload, petsToComplete, animals, uuid])

  const enabled =
    !!uuid &&
    !!computedCartPayload?.petsitters?.length &&
    !!computedCartPayload?.products?.length &&
    !!computedCartPayload?.animals?.length

  const queryKey = [
    'petsitter-price',
    uuid,
    computedCartPayload?.petsitters,
    computedCartPayload?.products,
    computedCartPayload?.animals,
  ]

  const query = useQuery(
    queryKey,
    async () => {
      try {
        return await getPriceAsync(uuid, computedCartPayload)
      } catch (error) {
        console.error('Error fetching pet sitter price:', error)

        addNotificationError({ error })

        throw error
      }
    },
    {
      enabled,
      staleTime: 60000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  )

  const petsitterPrice = query.data

  const reduction = petsitterPrice?.securityFees?.price === 0 ? petsitterPrice?.defaultSecurityFee?.price : null

  const quantity = getQuantity(petsitterPrice?.orderDetails?.[0])

  return {
    ...query,

    computedCartPayload,

    petsitterPrice,

    reduction,

    quantity,
  }
}
